import styled from '@emotion/styled';
import { Luggage } from '@innovationdepartment/proxima-ui';
import {
  AccountBalance,
  Bed,
  Build,
  Church,
  Elderly,
  Factory,
  Flag,
  Grain,
  Hub,
  Landscape,
  LightMode,
  LocationOn,
  MilitaryTech,
  Museum,
  Palette,
  PrecisionManufacturing,
  School,
  Signpost,
  Surfing,
  WbSunny
} from '@mui/icons-material';
import useHandleApi from 'hooks/useHandleApi';
import { type ReactNode, useEffect, useState } from 'react';
import useBrandStore from 'stores/useBrandStore';
import LoadingSpinner from 'ui/LoadingSpinner';
import BrandPersonasView from './BrandPersonas.View';

const LoadingSpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
`;

const BrandPersonas = () => {
  const { brand } = useBrandStore();
  const [brandPersonasData, setBrandPersonasData] = useState({
    personasData: {
      personas: []
    },
    crossBrandPurchases: []
  });
  const apiDomain = import.meta.env.VITE_PLATFORM_API_URL;
  const { handleApi, loading: personasLoading } = useHandleApi({ apiDomain });

  const getLtvTagClass = (vsAverageText: string): 'positive' | 'neutral' | 'negative' => {
    if (vsAverageText.includes('↑')) {
      return 'positive';
    }

    const percentMatch = vsAverageText.match(/(\d+)%/);
    if (percentMatch) {
      const percent = parseInt(percentMatch[1], 10);
      if (
        (vsAverageText.includes('↓') ||
          vsAverageText.toLowerCase().includes('below') ||
          (vsAverageText.toLowerCase().includes('vs average') && !vsAverageText.includes('↑'))) &&
        percent >= 10
      ) {
        return 'negative';
      }
    }

    return 'neutral';
  };

  const formatSettlementTitle = (key: string): string => {
    const titleMap: Record<string, string> = {
      HISTORIC: 'Historic Communities',
      BEDROOM_COMMUNITY: 'Bedroom Communities',
      INDUSTRIAL: 'Industrial Centers',
      SUN_BELT: 'Sun Belt Communities',
      TECH_HUB: 'Tech Hub Communities',
      PLANNED_COMMUNITY: 'Planned Communities'
    };
    return (
      titleMap[key] ||
      key
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase())
    );
  };

  const getSettlementIcon = (key: string): ReactNode => {
    const iconMap: Record<string, ReactNode> = {
      MOUNTAIN: <Landscape />,
      BEACH_COASTAL: <Surfing />,
      DESERT: <WbSunny />,
      GATEWAY_TOWN: <Signpost />,
      SUN_BELT: <LightMode />,
      BIBLE_BELT: <Church />,
      CORN_BELT: <Grain />,
      RUST_BELT: <Factory />,
      TECH_HUB: <Hub />,
      INDUSTRIAL: <PrecisionManufacturing />,
      AGRICULTURAL: <Grain />,
      MILITARY_PRESENCE: <MilitaryTech />,
      STATE_CAPITAL: <AccountBalance />,
      COLLEGE_TOWN: <School />,
      HISTORIC: <Museum />,
      ARTSY_BOHEMIAN: <Palette />,
      TOURISM_RESORT: <Luggage />,
      RETIREMENT_HAVEN: <Elderly />,
      BLUE_COLLAR: <Build />,
      BORDER_TOWN: <Flag />,
      PLANNED_COMMUNITY: <Build />,
      BEDROOM_COMMUNITY: <Bed />
    };

    return iconMap[key] || <LocationOn />;
  };

  const formatDateRange = (startDate: string, endDate: string): string => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];

    const formatDate = (dateString: string) => {
      const [year, month] = dateString.split('-').map(Number);
      return `${months[month - 1]} ${year}`;
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    const fetchBrandPersonas = async () => {
      const brandPersonasResponse = await handleApi({
        endpoint: `/brand/${brand.brandId}/persona`,
        method: 'GET',
        skipErrorToaster: true
      });

      const crossBrandPurchasesResponse = await handleApi({
        endpoint: `/brand/${brand.brandId}/personas-cross-brand-purchases`,
        method: 'GET',
        skipErrorToaster: true
      });

      setBrandPersonasData({
        personasData: brandPersonasResponse.data.data,
        crossBrandPurchases: crossBrandPurchasesResponse.data
      });
    };
    fetchBrandPersonas();
  }, [brand.brandId]);

  if (personasLoading) {
    return (
      <LoadingSpinnerWrapper>
        <LoadingSpinner />
      </LoadingSpinnerWrapper>
    );
  }

  return (
    <BrandPersonasView
      brandPersonasData={brandPersonasData}
      getLtvTagClass={getLtvTagClass}
      formatSettlementTitle={formatSettlementTitle}
      getSettlementIcon={getSettlementIcon}
      formatDateRange={formatDateRange}
    />
  );
};

export default BrandPersonas;
